import { Component, inject, input, signal } from "@angular/core";
import { Router, RouterLink, RouterLinkActive } from "@angular/router";
import { ROUTES } from "../../constants/routes";
import { SITE } from "../../constants/site";
import { AuthService } from "../../services/auth.service";

interface NavChild {
  label: string;
  description: string;
  link: string;
}

interface DashboardNavItem {
  label: string;
  link: string;
}

@Component({
  selector: "app-navbar",
  imports: [RouterLink, RouterLinkActive],
  templateUrl: "./navbar.html",
  styleUrl: "./navbar.scss"
})
export class Navbar {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  readonly isDashboardView = input(false);
  readonly routes = ROUTES;

  readonly site = SITE;
  readonly mobileOpen = signal(false);
  readonly openGroup = signal<string | null>(null);

  readonly adminNavItems: DashboardNavItem[] = [{ label: "Data Files", link: ROUTES.adminFiles.url }];

  readonly userNavItems: DashboardNavItem[] = [
    {
      label: "Bulk Verification",
      link: ROUTES.dashboardBulkVerification.url
    },
    {
      label: "Instant Verification",
      link: ROUTES.dashboardInstantVerification.url
    },
    {
      label: "Wallet",
      link: ROUTES.dashboardWallet.url
    }
  ];

  readonly solutions: NavChild[] = [
    {
      label: "Banks and credit unions",
      description: "Block deceased account takeover and new account fraud.",
      link: ROUTES.solutionsBanks.url
    },
    {
      label: "Lenders and card issuers",
      description: "Screen applicants against confirmed deceased records.",
      link: ROUTES.solutionsLenders.url
    },
    {
      label: "Employers and background checks",
      description: "Stop deceased identity use in hiring and onboarding.",
      link: ROUTES.solutionsEmployers.url
    },
    {
      label: "Insurance carriers",
      description: "Confirm date of death for claims and policy reviews.",
      link: ROUTES.solutionsInsurance.url
    },
    {
      label: "Government and public sector",
      description: "Prevent benefit and entitlement payment fraud.",
      link: ROUTES.solutionsGovernment.url
    },
    {
      label: "Collections and recovery",
      description: "Suppress deceased accounts before you contact them.",
      link: ROUTES.solutionsCollections.url
    }
  ];

  readonly resources: NavChild[] = [
    {
      label: "Guides and research",
      description: "Deep dives on deceased fraud, data quality and compliance.",
      link: ROUTES.resources.url
    },
    {
      label: "How it works",
      description: "From file upload to verified matches in minutes.",
      link: ROUTES.howItWorks.url
    },
    {
      label: "Developers and API",
      description: "Real time death verification for your systems.",
      link: ROUTES.developers.url
    },
    {
      label: "Security and compliance",
      description: "GLBA mapping, encryption, retention and vendor review.",
      link: ROUTES.security.url
    },
    {
      label: "FAQ",
      description: "Answers on coverage, matching and pricing.",
      link: ROUTES.faq.url
    },
    {
      label: "About us",
      description: "Powered by LeadCruncher property data.",
      link: ROUTES.about.url
    }
  ];

  get isLoggedIn(): boolean {
    return this.authService.isAuthenticated();
  }

  get isAdmin(): boolean {
    return this.authService.getClaims()?.role === "Admin";
  }

  get roleNavItems(): DashboardNavItem[] {
    return this.isAdmin ? this.adminNavItems : this.userNavItems;
  }

  get dashboardUrl(): string {
    return this.authService.getRoleBasedHomeUrl();
  }

  toggleMobile(): void {
    this.mobileOpen.update(open => !open);
    this.openGroup.set(null);
  }

  closeMobile(): void {
    this.mobileOpen.set(false);
    this.openGroup.set(null);
  }

  toggleGroup(group: string): void {
    this.openGroup.update(current => (current === group ? null : group));
  }

  logout(): void {
    this.authService.clearSession();
    this.closeMobile();
    void this.router.navigateByUrl(ROUTES.home.url);
  }
}
